import React, { useState, useRef, useEffect } from 'react';
import './RichTextEditor.css';

const COLOR_SWATCHES = ['#1e293b', '#d92626', '#1d7a2e', '#1d5fd9', '#a3760f', '#7c1dd9'];

// A small execCommand-based rich text box -- enough for bold/italic/underline/color/lists/links
// without pulling in a WYSIWYG library. Output is untrusted client HTML; the backend always
// re-sanitizes (email_service.sanitize_html) before storing or sending it, so nothing here needs
// to be airtight on its own -- it just needs to expose a safe, small set of controls. Shared by
// Bulk Email and My Class -- both compose an HTML email body the same way.
const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);
  const [showColors, setShowColors] = useState(false);
  // Tracks whether the pending `value` change came from this editor's own typing (already in
  // the DOM) vs. an external reset (e.g. the parent clearing the body after a send) -- syncing
  // innerHTML from `value` on every keystroke would reset the caret to position 0 each time
  // (each new DOM replacement forgets the old selection), so typing "123" would land as "321".
  const isInternalUpdate = useRef(false);

  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const notifyChange = () => {
    isInternalUpdate.current = true;
    onChange(ref.current.innerHTML);
  };

  const exec = (command, arg) => {
    ref.current.focus();
    document.execCommand(command, false, arg);
    notifyChange();
  };

  const insertLink = () => {
    const url = window.prompt('Link URL (https://...)');
    if (url) exec('createLink', url);
  };

  return (
    <div className="rte-container">
      <div className="rte-toolbar">
        <button type="button" onClick={() => exec('bold')} title="Bold"><b>B</b></button>
        <button type="button" onClick={() => exec('italic')} title="Italic"><i>I</i></button>
        <button type="button" onClick={() => exec('underline')} title="Underline"><u>U</u></button>
        <span className="rte-divider" />
        <button type="button" onClick={() => exec('insertUnorderedList')} title="Bullet list">&bull; List</button>
        <button type="button" onClick={() => exec('insertOrderedList')} title="Numbered list">1. List</button>
        <span className="rte-divider" />
        <button type="button" onClick={insertLink} title="Insert link">Link</button>
        <span className="rte-divider" />
        <div className="rte-color-picker">
          <button type="button" onClick={() => setShowColors(s => !s)} title="Text color">A</button>
          {showColors && (
            <div className="rte-color-swatches">
              {COLOR_SWATCHES.map(c => (
                <button
                  key={c} type="button" className="rte-swatch" style={{ background: c }}
                  onClick={() => { exec('foreColor', c); setShowColors(false); }}
                />
              ))}
            </div>
          )}
        </div>
        <button type="button" onClick={() => exec('removeFormat')} title="Clear formatting">Clear</button>
      </div>
      <div
        ref={ref}
        className="rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={notifyChange}
      />
    </div>
  );
};

export default RichTextEditor;
